import * as swcHelpers from '@swc/helpers';
import * as React from 'react';
import { toast } from '@grandvisionhq/common';
import { useMutation } from '@grandvisionhq/graphql';
import { useCartStorage } from '@grandvisionhq/lib-utils-cart';
import '@grandvisionhq/utils';
import { errorsMapping } from '../../mappers/errors-mapping.js';
import { SET_CUSTOMER_DETAILS } from '../../operations/mutations/set-customer-details.js';
import '../../operations/mutations/set-shipping-method.js';
import '../../operations/mutations/set-shipping-state.js';
import '../../operations/mutations/set-store.js';
import '../../operations/mutations/create-apple-pay-session.js';
import '../../operations/mutations/recreate-ordered-checkout.js';
import 'lodash/isEmpty';
import { getFlattenedCartFailureErrorCodes } from '../../utils/get-flattened-cart-error-codes.js';
import '../../utils/get-customer-details-payload.js';
import { trimInputData } from '../../utils/trim-input-data.js';
import '../../models/cart/cart-state.js';
import '../../models/checkout/checkout-step-error-type.js';
import '../../models/checkout-settings.js';
import '../../models/step-shipping/shipping-methods.js';
import '../../models/shared.js';
import 'history';
import '../../utils/has-cart-errors.js';
import { useMutationConfig } from './use-mutation-config.js';
import { useUpdateCartCache } from './use-update-cart-cache.js';

const useSetCustomerDetails = ({ onError , onCompleted  } = {})=>{
    const [cart] = useCartStorage();
    const { updateCacheWithCart  } = useUpdateCartCache();
    /**
   * Handle success and error response from mutation operation
   * @param responseData
   */ const handleOnCompleted = React.useCallback((responseData)=>{
        let result;
        if ('setCustomerDetails' in responseData) {
            var ref;
            result = responseData.setCustomerDetails;
            if ((ref = result.failures) === null || ref === void 0 ? void 0 : ref.length) {
                const errorCodes = getFlattenedCartFailureErrorCodes(result.failures);
                onError === null || onError === void 0 ? void 0 : onError({
                    reason: 'MUTATION'
                });
                /**
           *  Notify user about all failures
           *  To handle granular errors return from mutation operation
           */ errorCodes.forEach((error)=>toast.failed(error)
                );
            }
            if (result.success) {
                onCompleted === null || onCompleted === void 0 ? void 0 : onCompleted('MUTATION');
            }
        }
    }, [
        onCompleted,
        onError
    ]);
    const handleOnError = React.useCallback(()=>{
        onError === null || onError === void 0 ? void 0 : onError({
            reason: 'MUTATION'
        });
        // General error for apollo error response
        toast.failed(errorsMapping.SOMETHING_WENT_WRONG);
    }, [
        onError
    ]);
    const updateCache = React.useCallback((cache, { data  })=>{
        if (data && data.setCustomerDetails.success) {
            const updatedCart = data.setCustomerDetails.cart;
            updatedCart && updateCacheWithCart({
                cart: updatedCart,
                cache
            });
        }
    }, [
        updateCacheWithCart
    ]);
    const { mutationOptions  } = useMutationConfig({
        onError: handleOnError,
        onCompleted: handleOnCompleted
    });
    const [mutationCallback, mutation] = useMutation(SET_CUSTOMER_DETAILS, swcHelpers.objectSpread({}, mutationOptions, {
        // reason: typescript complains about a typing mismatch for the data prop
        update: updateCache,
        context: {
            forwardHeaders: {
                'cart-hash': cart.cartHash
            }
        }
    }));
    /**
   *  Customer details data update operation
   */ const setCustomerDetails = React.useCallback(function() {
        var _ref = swcHelpers.asyncToGenerator(function*(customerDetails) {
            if (!cart.cartId) {
                throw new Error('Need to have valid cart to update!');
            }
            const sanitizedData = trimInputData(customerDetails);
            return yield mutationCallback({
                variables: {
                    customerDetailsInput: swcHelpers.objectSpread({}, sanitizedData, {
                        checkoutId: cart.cartId
                    })
                }
            }).then(({ data  })=>{
                return {
                    success: data === null || data === void 0 ? void 0 : data.setCustomerDetails.success
                };
            });
        });
        return function(customerDetails) {
            return _ref.apply(this, arguments);
        };
    }(), [
        cart.cartId,
        mutationCallback
    ]);
    return {
        setCustomerDetails,
        isUpdating: mutation.loading,
        updateError: mutation.error
    };
};

export { useSetCustomerDetails };
//# sourceMappingURL=use-set-customer-details.js.map
